import type { AttentionItem } from '@/composables/useNeedsAttention';

/**
 * AB#21985 — how urgent a card is, decided by rule on the server:
 *   delayed  red    late or blocked (ship date passed, ELSA red, …)
 *   at_risk  amber  ELSA orange, or a date / factory still to be entered
 *   watch    blue   nothing wrong: ships soon on a green read, or just quiet
 * Every screen colours and counts by this, not by the severity band.
 */
export type AttentionLevel = 'delayed' | 'at_risk' | 'watch';
export const ATTENTION_LEVELS: AttentionLevel[] = ['delayed', 'at_risk', 'watch'];
export const LEVEL_LABEL: Record<AttentionLevel, string> = {
  delayed: 'Delayed',
  at_risk: 'At risk',
  watch: 'Keep an eye',
};
export type LevelCounts = Record<AttentionLevel, number>;
export const emptyLevels = (): LevelCounts => ({ delayed: 0, at_risk: 0, watch: 0 });

/**
 * The card's level. A backend that predates levels sends only the band; that
 * is read the way it was coloured (critical red, high amber, medium blue), so
 * nothing changes on screen until the backend follows.
 */
export const levelOf = (item: Pick<AttentionItem, 'level' | 'band'>): AttentionLevel => {
  if (item.level && ATTENTION_LEVELS.includes(item.level)) return item.level;
  if (item.band === 'critical') return 'delayed';
  return item.band === 'medium' ? 'watch' : 'at_risk';
};

export const countLevels = (
  list: ReadonlyArray<Pick<AttentionItem, 'level' | 'band'>>
): LevelCounts => {
  const out = emptyLevels();
  for (const item of list) out[levelOf(item)] += 1;
  return out;
};

export const readLevels = (raw: unknown): LevelCounts | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<Record<AttentionLevel, unknown>>;
  const n = (v: unknown): number => (typeof v === 'number' && v >= 0 ? v : 0);
  return { delayed: n(r.delayed), at_risk: n(r.at_risk), watch: n(r.watch) };
};
